import React, { useEffect, useMemo, useState } from 'react';
import HighlightedTTS from './HighlightedTTS';
import MathContent, { MathExpression } from './MathContent';
import { mathExpressionToSpeech } from '../utils/mathContent';

const answerKeys = new Set([
  'CorrectSelections', 'Answer', 'CorrectParts', 'CorrectPair',
  'CorrectEquation', 'CorrectPoints', 'Tolerance', 'RequiredUnit', 'Units',
  'Tokens', 'Min', 'Max', 'Step', 'PointCount', 'OrderMatters', 'Passage',
  'MinimumWCPM', 'MinimumAccuracy', 'MinimumFluency', 'PartA', 'PartB', 'Parts'
]);

const asList = value => {
  const unwrapped = value && typeof value === 'object' && !Array.isArray(value)
    ? value.text
    : value;
  if (Array.isArray(unwrapped)) {
    return unwrapped.map(item => String(item?.text ?? item ?? '').trim()).filter(Boolean);
  }
  if (unwrapped == null) return [];
  return String(unwrapped).split(/\r?\n|\s*[,|]\s*/).map(item => item.trim()).filter(Boolean);
};

const safeParse = (value, fallback) => {
  if (value && typeof value === 'object') return value;
  try { return JSON.parse(String(value || '')); } catch { return fallback; }
};

const parseMatchingResponse = value => {
  const parsed = safeParse(value, null);
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
  return String(value || '').split(',').reduce((pairs, entry) => {
    const separator = entry.indexOf(':');
    if (separator < 1) return pairs;
    const left = entry.slice(0, separator).trim();
    const right = entry.slice(separator + 1).trim();
    if (left && right) pairs[left] = right;
    return pairs;
  }, {});
};

const hotspotDefinitions = value => {
  const raw = Array.isArray(value) ? value : asList(value);
  return raw.map((hotspot, index) => {
    if (!hotspot || typeof hotspot !== 'object') {
      const label = String(hotspot || '').trim();
      return { id: label, label };
    }
    const id = String(hotspot.id ?? hotspot.value ?? hotspot.key ?? hotspot.label ?? hotspot.text ?? index + 1).trim();
    const label = String(hotspot.label ?? hotspot.text ?? hotspot.name ?? id).trim();
    const number = key => Number.isFinite(Number(hotspot[key])) ? Number(hotspot[key]) : null;
    return {
      id,
      label,
      x: number('x'),
      y: number('y'),
      width: number('width'),
      height: number('height'),
    };
  }).filter(hotspot => hotspot.id && hotspot.label);
};

const hotspotColor = label => {
  const colors = ['purple', 'yellow', 'red', 'blue', 'green', 'orange', 'pink', 'black', 'white', 'gray', 'grey', 'brown'];
  const match = colors.find(color => String(label).toLowerCase().includes(color));
  return match === 'grey' ? 'gray' : match || null;
};

const visibleOptions = options => Object.entries(options || {})
  .filter(([key, value]) => !answerKeys.has(key) && /^[A-Za-z0-9_-]+$/.test(key))
  .filter(([, value]) => value !== null && value !== undefined && String(value?.text ?? value).trim() !== '')
  .map(([key, value]) => ({
    key,
    text: String(value?.text ?? value),
    speech: value && typeof value === 'object' ? (value.speech || value.MathSpeech || '') : ''
  }));

const panel = {
  border: '1px solid #cbd5e1', borderRadius: '0.65rem', padding: '1rem',
  marginBottom: '1rem', background: '#fff'
};
const row = { display: 'flex', alignItems: 'center', gap: '0.65rem', margin: '0.55rem 0' };
const input = { width: '100%', minHeight: '3rem', padding: '0.65rem', fontSize: '1.1rem', border: '1px solid #94a3b8', borderRadius: '0.5rem' };
const action = { minHeight: '3rem', padding: '0.65rem 1rem', border: 0, borderRadius: '0.5rem', background: '#2563eb', color: '#fff', fontWeight: 700, cursor: 'pointer' };

const numericShortcutLabels = Object.freeze({
  '−': 'Insert minus sign',
  '/': 'Insert fraction slash',
  '.': 'Insert decimal point',
  '%': 'Insert percent sign'
});

const clamp = (value, minimum, maximum) => Math.max(minimum, Math.min(maximum, value));
const finiteNumber = (value, fallback) => Number.isFinite(Number(value)) ? Number(value) : fallback;
const rationalNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  let text = String(value ?? '').normalize('NFKC').trim().replace(/⁄/g, '/').replace(/[−–—]/g, '-');
  let percent = false;
  if (text.endsWith('%')) { percent = true; text = text.slice(0, -1).trim(); }
  const mixed = text.match(/^([+-]?\d+)\s+(\d+)\/(\d+)$/);
  const fraction = text.match(/^([+-]?\d+)\/(\d+)$/);
  let result = null;
  if (mixed && Number(mixed[3]) !== 0) {
    const whole = Number(mixed[1]);
    result = whole < 0 ? whole - Number(mixed[2]) / Number(mixed[3]) : whole + Number(mixed[2]) / Number(mixed[3]);
  } else if (fraction && Number(fraction[2]) !== 0) {
    result = Number(fraction[1]) / Number(fraction[2]);
  } else if (/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(text)) result = Number(text);
  if (!Number.isFinite(result)) return null;
  return percent ? result / 100 : result;
};
const decimalPlaces = (value) => {
  const text = String(value);
  return text.includes('.') ? Math.min(8, text.split('.')[1].length) : 0;
};
const snappedValue = (value, minimum, maximum, step = 1) => {
  const safeStep = finiteNumber(step, 1) > 0 ? finiteNumber(step, 1) : 1;
  const snapped = minimum + Math.round((clamp(value, minimum, maximum) - minimum) / safeStep) * safeStep;
  return Number(clamp(snapped, minimum, maximum).toFixed(Math.max(decimalPlaces(safeStep), 6)));
};
const tickValues = (minimum, maximum, preferredStep, maximumTicks = 21) => {
  const span = maximum - minimum;
  if (!(span > 0)) return [];
  let step = finiteNumber(preferredStep, span / 10);
  if (!(step > 0) || span / step > maximumTicks - 1) step = span / Math.min(10, maximumTicks - 1);
  const ticks = [];
  for (let index = 0; index <= maximumTicks; index += 1) {
    const value = minimum + index * step;
    if (value > maximum + step / 1000) break;
    ticks.push(Number(Math.min(value, maximum).toFixed(6)));
  }
  if (ticks[ticks.length - 1] !== maximum) ticks.push(maximum);
  return [...new Set(ticks)];
};

const optionSpeech = option => option?.speech || option?.MathSpeech || '';

function ChoiceGroup({ title, titleSpeech = '', options, value, onChange, multiple = false, disabled, ttsEnabled = false }) {
  const selected = multiple ? new Set(Array.isArray(value) ? value : []) : null;
  return <fieldset style={panel} disabled={disabled}>
    <legend style={{ fontWeight: 700, padding: '0 0.35rem' }}><MathContent text={title} /></legend>
    <HighlightedTTS text={title} speechText={titleSpeech} enabled={ttsEnabled} size="small" hideText ariaLabel={`Read ${title} aloud`} />
    {options.map(option => {
      const isSelected = multiple ? selected.has(option.key) : value === option.key;
      return <label key={option.key} style={row} className={`assessment-choice${isSelected ? ' assessment-choice-selected' : ''}`}>
      <input
        type={multiple ? 'checkbox' : 'radio'}
        name={multiple ? undefined : title}
        checked={isSelected}
        onChange={() => {
          if (!multiple) return onChange(option.key);
          const next = new Set(selected);
          next.has(option.key) ? next.delete(option.key) : next.add(option.key);
          onChange([...next]);
        }}
      />
      <HighlightedTTS
        text={option.text}
        speechText={optionSpeech(option)}
        enabled={ttsEnabled}
        size="small"
        ariaLabel={`Read option ${option.text} aloud`}
        textStyle={{ fontSize: '1.1rem', color: '#1f2937' }}
        highlightedStyle={{ backgroundColor: '#fef08a' }}
      />
    </label>})}
  </fieldset>;
}

// Structured controls only edit a local draft. AssessmentQuestion owns the
// single commit boundary: Next validates, saves/scores, persists the cursor,
// and only then advances.
function CommitButton() { return null; }

function structuredDraftComplete(type, options, draft) {
  if (draft === null || draft === undefined) return false;
  if (type === 'matching') {
    const left = asList(options.LeftItems);
    const matches = draft && typeof draft === 'object' && !Array.isArray(draft) ? draft : {};
    return left.length > 0 && left.every(item => matches[item])
      && new Set(left.map(item => matches[item])).size === left.length;
  }
  if (type === 'dragAndDrop') {
    const items = asList(options.Items);
    const targets = new Set(asList(options.Targets));
    return items.length > 0 && Array.isArray(draft) && draft.length >= items.length
      && draft.slice(0, items.length).every(value => targets.has(value));
  }
  if (type === 'multipleSelect') return Array.isArray(draft) && draft.length > 0;
  if (type === 'numericResponse') {
    const value = draft && typeof draft === 'object' ? draft : { value: draft, unit: '' };
    return String(value.value ?? '').trim() !== ''
      && (!asList(options.Units).length || String(value.unit ?? '').trim() !== '');
  }
  if (type === 'evidencePair') return Boolean(draft?.partA && draft?.partB);
  if (type === 'multiPart') {
    const parts = Array.isArray(options.Parts) ? options.Parts : [];
    return parts.length > 0 && parts.every((part, index) => {
      const answer = draft?.[String(part.id ?? index + 1)];
      if ((part.questionType || 'multipleChoice') === 'numericResponse' && answer && typeof answer === 'object') {
        return String(answer.value ?? '').trim() !== ''
          && (!asList(part.Options?.Units).length || String(answer.unit ?? '').trim() !== '');
      }
      return Array.isArray(answer) ? answer.length > 0 : String(answer ?? '').trim() !== '';
    });
  }
  if (type === 'numberLine') {
    const count = Math.max(1, Math.min(10, Number(options.PointCount || 1)));
    return Array.isArray(draft) && draft.slice(0, count).length === count
      && draft.slice(0, count).every(value => String(value ?? '').trim() !== '');
  }
  if (type === 'coordinateGraph') {
    const count = Math.max(1, Math.min(10, Number(options.PointCount || 1)));
    return Array.isArray(draft) && draft.slice(0, count).length === count
      && draft.slice(0, count).every(point => String(point?.x ?? '').trim() !== '' && String(point?.y ?? '').trim() !== '');
  }
  if (type === 'oralReading') return true;
  return String(draft ?? '').trim() !== '';
}

function defaultNumericUnit(question, options) {
  const units = asList(options.Units);
  const requested = String(options.RequiredUnit ?? question.RequiredUnit ?? '').trim();
  if (requested && units.includes(requested)) return requested;
  return units[0] || requested;
}

export default function AdvancedQuestionRenderer({
  question,
  storedResponse,
  disabled,
  onCommit,
  onDraftChange,
  ttsEnabled = false,
}) {
  const type = question.questionType || question.QuestionType;
  const options = question.Options || {};
  const itemKey = String(question.ItemNumber);
  const [draft, setDraft] = useState(null);
  const [committed, setCommitted] = useState(Boolean(storedResponse));
  const [activePointIndex, setActivePointIndex] = useState(0);
  const [selectedDragItem, setSelectedDragItem] = useState('');

  useEffect(() => {
    setCommitted(Boolean(storedResponse));
    if (type === 'matching') setDraft(parseMatchingResponse(storedResponse));
    else if (type === 'dragAndDrop') setDraft(asList(safeParse(storedResponse, storedResponse)));
    else if (type === 'multipleSelect') setDraft(asList(safeParse(storedResponse, storedResponse)));
    else if (type === 'numericResponse') {
      const restored = safeParse(storedResponse, { value: storedResponse || '', unit: '' });
      setDraft(restored && typeof restored === 'object'
        ? { ...restored, unit: restored.unit || defaultNumericUnit(question, options) }
        : { value: String(restored || ''), unit: defaultNumericUnit(question, options) });
    }
    else if (type === 'evidencePair') setDraft(safeParse(storedResponse, { partA: '', partB: '' }));
    else if (type === 'multiPart') setDraft(safeParse(storedResponse, {}));
    else if (type === 'numberLine') setDraft(safeParse(storedResponse, ['']));
    else if (type === 'coordinateGraph') setDraft(safeParse(storedResponse, [{ x: '', y: '' }]));
    else setDraft(storedResponse || '');
    setActivePointIndex(0);
    setSelectedDragItem('');
  }, [itemKey, storedResponse, type]);

  // Keep the parent aware of both the exact on-screen value and whether every
  // required part is complete. Next remains the only save/submit control.
  useEffect(() => {
    if (draft === null || !onDraftChange) return;
    const value = type === 'oralReading' ? 'awaiting-teacher-score' : draft;
    const serialized = typeof value === 'string' ? value : JSON.stringify(value);
    onDraftChange(itemKey, serialized, structuredDraftComplete(type, options, value));
  }, [draft, itemKey, onDraftChange, options, type]);

  const commit = async value => {
    const serialized = typeof value === 'string' ? value : JSON.stringify(value);
    await onCommit(serialized);
    setCommitted(true);
  };

  const optionList = useMemo(() => visibleOptions(options), [options]);

  if (type === 'matching') {
    const leftItems = asList(options.LeftItems);
    const rightItems = asList(options.RightItems);
    const matches = draft && typeof draft === 'object' && !Array.isArray(draft) ? draft : {};
    const selectedRights = new Set(Object.values(matches));
    const complete = leftItems.length > 0
      && leftItems.every(left => matches[left])
      && new Set(leftItems.map(left => matches[left])).size === leftItems.length;
    return <div style={panel}>
      <p style={{ marginTop: 0, color: '#475569' }}>Choose one match for each item. Each choice can be used once.</p>
      {leftItems.map((left, index) => <div key={left} className="assessment-matching-row" style={{
        display: 'grid', gridTemplateColumns: 'minmax(8rem, 1fr) auto minmax(12rem, 2fr)',
        alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem'
      }}>
        <div style={{ ...panel, margin: 0, fontWeight: 700, display: 'flex', alignItems: 'center', gap: '.35rem' }}>
          <HighlightedTTS text={left} enabled={ttsEnabled} size="small" hideText ariaLabel={`Read matching item ${index + 1} aloud`} />
          <MathContent text={left} />
        </div>
        <span aria-hidden="true" style={{ fontSize: '1.5rem' }}>→</span>
        <div role="group" aria-label={`Match for ${left}`} style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
          {rightItems.map(right => {
            const selected = matches[left] === right;
            const usedElsewhere = selectedRights.has(right) && !selected;
            return <div key={right} style={{ display: 'inline-flex', alignItems: 'center', gap: '.25rem' }}>
              <button type="button" disabled={disabled || usedElsewhere} aria-pressed={selected}
                onClick={() => {
                  const next = { ...matches };
                  if (selected) delete next[left]; else next[left] = right;
                  setDraft(next); setCommitted(false);
                }} style={{ ...action, background: selected ? '#7c3aed' : '#2563eb', opacity: usedElsewhere ? 0.35 : 1 }}>
                <MathContent text={right} />
              </button>
              <HighlightedTTS text={right} enabled={ttsEnabled} size="small" hideText ariaLabel={`Read matching choice ${right} aloud`} />
            </div>;
          })}
        </div>
      </div>)}
      <CommitButton disabled={disabled || !complete} committed={committed} onClick={() => commit(matches)} />
    </div>;
  }

  if (type === 'dragAndDrop') {
    const items = asList(options.Items);
    const targets = asList(options.Targets);
    const placements = Array.isArray(draft) ? draft.slice(0, items.length) : [];
    while (placements.length < items.length) placements.push('');
    const placeItem = (item, target) => {
      const index = items.indexOf(item);
      if (index < 0 || !targets.includes(target)) return;
      const next = [...placements];
      next[index] = target;
      setDraft(next);
      setCommitted(false);
      setSelectedDragItem('');
    };
    const itemButton = item => {
      const selected = selectedDragItem === item;
      return <div key={item} style={{ display: 'inline-flex', alignItems: 'center', gap: '.2rem' }}>
        <button type="button" draggable={!disabled}
          aria-pressed={selected} disabled={disabled}
          onDragStart={event => { event.dataTransfer.setData('text/plain', item); setSelectedDragItem(item); }}
          onClick={event => { event.stopPropagation(); setSelectedDragItem(selected ? '' : item); }}
          style={{ ...action, background: selected ? '#7c3aed' : '#2563eb', margin: '0.25rem' }}>
          <MathContent text={item} />
        </button>
        <HighlightedTTS text={item} enabled={ttsEnabled} size="small" hideText ariaLabel={`Read draggable item ${item} aloud`} />
      </div>;
    };
    return <div style={panel}>
      <p style={{ marginTop: 0, color: '#475569' }}>Drag each item to a category, or tap an item and then tap its category.</p>
      <div aria-label="Items not yet placed" style={{ ...panel, background: '#f8fafc' }}>
        <strong>Items to place</strong>
        <div>{items.filter((item, index) => !placements[index]).map(itemButton)}</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(targets.length, 3)}, minmax(10rem, 1fr))`, gap: '0.75rem' }}>
        {targets.map(target => <section key={target} tabIndex={disabled ? -1 : 0}
          aria-label={`${target} category`} onClick={() => selectedDragItem && placeItem(selectedDragItem, target)}
          onDragOver={event => event.preventDefault()}
          onDrop={event => { event.preventDefault(); placeItem(event.dataTransfer.getData('text/plain'), target); }}
          onKeyDown={event => {
            if ((event.key === 'Enter' || event.key === ' ') && selectedDragItem) {
              event.preventDefault(); placeItem(selectedDragItem, target);
            }
          }}
          style={{ ...panel, minHeight: '8rem', borderWidth: 2, borderStyle: 'dashed', background: '#eff6ff' }}>
          <div style={{ marginTop: 0, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '.3rem', fontSize: '1.17em', fontWeight: 700 }}>
            <HighlightedTTS text={target} enabled={ttsEnabled} size="small" hideText ariaLabel={`Read category ${target} aloud`} />
            <MathContent text={target} />
          </div>
          <div>{items.filter((item, index) => placements[index] === target).map(itemButton)}</div>
        </section>)}
      </div>
      <CommitButton disabled={disabled || placements.some(placement => !placement)} committed={committed}
        onClick={() => commit(placements)} />
    </div>;
  }

  if (type === 'hotspot') {
    const hotspots = hotspotDefinitions(options.Hotspots);
    const selected = String(draft || '');
    const imageUrl = String(options.Image || '');
    const positioned = hotspots.length > 0 && hotspots.every(hotspot => (
      Number.isFinite(hotspot.x) && Number.isFinite(hotspot.y)
      && Number.isFinite(hotspot.width) && Number.isFinite(hotspot.height)
      && hotspot.width > 0 && hotspot.height > 0
    ));
    const choose = hotspot => { setDraft(hotspot.id); setCommitted(false); };
    return <div style={panel}>
      <p style={{ marginTop: 0, color: '#475569' }}>
        {positioned ? 'Select the correct area directly on the image.' : 'Select the correct labeled region.'}
      </p>
      {imageUrl && <div style={{ position: 'relative', display: 'inline-block', maxWidth: '100%', marginBottom: '1rem' }}>
        <img src={imageUrl.startsWith('http') || imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`}
          alt={options.ImageAlt || question.HotspotImageAlt || 'Image with selectable regions'}
          onError={event => { event.currentTarget.style.display = 'none'; }}
          style={{ display: 'block', maxWidth: '100%', maxHeight: '28rem', objectFit: 'contain' }} />
        {positioned && hotspots.map(hotspot => <button type="button" key={hotspot.id} disabled={disabled}
          aria-label={`Select ${hotspot.label}`} aria-pressed={selected === hotspot.id}
          onClick={() => choose(hotspot)} style={{
            position: 'absolute', left: `${hotspot.x}%`, top: `${hotspot.y}%`,
            width: `${hotspot.width}%`, height: `${hotspot.height}%`,
            border: selected === hotspot.id ? '4px solid #2563eb' : '2px dashed rgba(37,99,235,.75)',
            background: selected === hotspot.id ? 'rgba(37,99,235,.25)' : 'rgba(255,255,255,.08)',
            cursor: 'pointer', borderRadius: '0.4rem'
          }} />)}
      </div>}
      {!positioned && <div role="group" aria-label="Selectable hotspot regions" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
        {hotspots.map(hotspot => {
          const color = hotspotColor(hotspot.label);
          const isSelected = selected === hotspot.id;
          return <div key={hotspot.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '.25rem' }}>
            <button type="button" disabled={disabled} aria-pressed={isSelected}
              onClick={() => choose(hotspot)} style={{
                ...action, color: color === 'yellow' || color === 'white' ? '#0f172a' : '#fff',
                background: color || (isSelected ? '#7c3aed' : '#2563eb'),
                outline: isSelected ? '4px solid #2563eb' : 'none', outlineOffset: 2
              }}><span aria-hidden="true" style={{ width: '1.25rem', height: '1.25rem', borderRadius: '50%', background: color || '#cbd5e1', border: '1px solid #64748b' }} />
              <MathContent text={hotspot.label} />
            </button>
            <HighlightedTTS text={hotspot.label} enabled={ttsEnabled} size="small" hideText ariaLabel={`Read hotspot ${hotspot.label} aloud`} />
          </div>;
        })}
      </div>}
      {positioned && ttsEnabled && <div aria-label="Hotspot labels read aloud" style={{ display: 'flex', flexWrap: 'wrap', gap: '.35rem', marginBottom: '1rem' }}>
        {hotspots.map(hotspot => <HighlightedTTS key={hotspot.id} text={hotspot.label} enabled size="small" hideText ariaLabel={`Read hotspot ${hotspot.label} aloud`} />)}
      </div>}
      <CommitButton disabled={disabled || !selected} committed={committed} onClick={() => commit(selected)} />
    </div>;
  }

  if (type === 'multipleSelect') {
    const selections = Array.isArray(draft) ? draft : [];
    return <div>
      <ChoiceGroup title="Select every correct answer" options={optionList} value={selections}
        multiple disabled={disabled} ttsEnabled={ttsEnabled}
        onChange={value => { setDraft(value); setCommitted(false); }} />
      <CommitButton disabled={disabled || selections.length === 0} committed={committed} onClick={() => commit(selections)} />
    </div>;
  }

  if (type === 'numericResponse') {
    const units = asList(options.Units);
    const parsed = draft && typeof draft === 'object'
      ? { ...draft, unit: draft.unit || defaultNumericUnit(question, options) }
      : { value: String(draft || ''), unit: defaultNumericUnit(question, options) };
    return <div style={panel}>
      <label style={{ display: 'block', fontWeight: 700, marginBottom: '0.5rem' }}>Enter a number</label>
      <div style={{ display: 'grid', gridTemplateColumns: units.length ? '1fr minmax(7rem, auto)' : '1fr', gap: '0.5rem' }}>
        <input inputMode="decimal" style={input} value={parsed.value ?? ''} disabled={disabled}
          aria-label="Numeric response" autoComplete="off" spellCheck={false}
          onChange={event => { setDraft({ ...parsed, value: event.target.value }); setCommitted(false); }} />
        {units.length > 0 && <select aria-label="Response unit" style={input} value={parsed.unit} disabled={disabled}
          onChange={event => { setDraft({ ...parsed, unit: event.target.value }); setCommitted(false); }}>
          {units.map(unit => <option key={unit} value={unit}>{unit}</option>)}
        </select>}
      </div>
      {String(parsed.value ?? '').trim() && (
        <div aria-live="polite" style={{ marginTop: '0.75rem', padding: '0.65rem', background: '#f8fafc', borderRadius: '0.5rem', fontSize: '1.35rem' }}>
          <span style={{ fontSize: '0.85rem', color: '#475569', marginRight: '0.5rem' }}>Preview:</span>
          <MathExpression value={parsed.value} />
        </div>
      )}
      <div aria-label="Math entry shortcuts" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.65rem' }}>
        {['−', '/', '.', '%'].map(token => <button type="button" key={token} disabled={disabled}
          aria-label={numericShortcutLabels[token]} title={numericShortcutLabels[token]}
          style={{ ...action, minWidth: '3rem', padding: '0.45rem', background: '#475569' }}
          onClick={() => { setDraft({ ...parsed, value: `${parsed.value ?? ''}${token}` }); setCommitted(false); }}>{token}</button>)}
        <button type="button" disabled={disabled || !String(parsed.value ?? '')} style={{ ...action, background: '#64748b' }}
          aria-label="Delete last character" title="Delete last character"
          onClick={() => { setDraft({ ...parsed, value: String(parsed.value ?? '').slice(0, -1) }); setCommitted(false); }}>⌫</button>
      </div>
      <div style={{ marginTop: '0.75rem' }}><CommitButton disabled={disabled || String(parsed.value || '').trim() === ''}
        committed={committed} onClick={() => commit(units.length ? parsed : parsed.value)} /></div>
    </div>;
  }

  if (type === 'evidencePair') {
    const value = draft && typeof draft === 'object' ? draft : { partA: '', partB: '' };
    const partA = options.PartA || {};
    const partB = options.PartB || {};
    return <div>
      <ChoiceGroup title={`Part A: ${partA.Question || 'Choose an answer'}`} titleSpeech={partA.QuestionSpeech}
        options={visibleOptions(partA.Options)} value={value.partA} disabled={disabled} ttsEnabled={ttsEnabled}
        onChange={partAValue => { setDraft({ ...value, partA: partAValue }); setCommitted(false); }} />
      <ChoiceGroup title={`Part B: ${partB.Question || 'Choose supporting evidence'}`} titleSpeech={partB.QuestionSpeech}
        options={visibleOptions(partB.Options)} value={value.partB} disabled={disabled} ttsEnabled={ttsEnabled}
        onChange={partBValue => { setDraft({ ...value, partB: partBValue }); setCommitted(false); }} />
      <CommitButton disabled={disabled || !value.partA || !value.partB} committed={committed} onClick={() => commit(value)} />
    </div>;
  }

  if (type === 'multiPart') {
    const value = draft && typeof draft === 'object' ? draft : {};
    const parts = Array.isArray(options.Parts) ? options.Parts : [];
    return <div>
      {parts.map((part, index) => {
        const id = String(part.id ?? index + 1);
        const partType = part.questionType || 'multipleChoice';
        const choiceType = ['multipleChoice', 'trueFalse', 'multipleSelect'].includes(partType);
        const partValue = value[id] ?? (partType === 'multipleSelect' ? [] : '');
        const updatePart = nextValue => { setDraft({ ...value, [id]: nextValue }); setCommitted(false); };
        const partUnits = asList(part.Options?.Units);
        const equationTokens = asList(part.Options?.Tokens);
        return <div key={id} style={panel}>
          <HighlightedTTS text={`Part ${index + 1}: ${part.Question}`} speechText={part.QuestionSpeech}
            enabled={ttsEnabled} size="small" textStyle={{ fontWeight: 700 }} highlightedStyle={{ backgroundColor: '#fef08a' }} />
          {choiceType ? <ChoiceGroup title={`Part ${index + 1}`} options={visibleOptions(part.Options)} value={partValue}
            multiple={partType === 'multipleSelect'} disabled={disabled} ttsEnabled={ttsEnabled}
            onChange={updatePart} />
            : partType === 'numericResponse' ? <div style={{ display: 'grid', gridTemplateColumns: partUnits.length ? '1fr minmax(7rem, auto)' : '1fr', gap: '0.5rem' }}>
              <input type="text" inputMode="decimal" style={input}
                value={typeof partValue === 'object' ? partValue.value ?? '' : partValue} disabled={disabled}
                aria-label={`Part ${index + 1} numeric response`}
                onChange={event => updatePart(partUnits.length
                  ? { value: event.target.value, unit: partValue?.unit || partUnits[0] }
                  : event.target.value)} />
              {partUnits.length > 0 && <select style={input} disabled={disabled}
                value={typeof partValue === 'object' ? partValue.unit || partUnits[0] : partUnits[0]}
                onChange={event => updatePart({ value: partValue?.value || '', unit: event.target.value })}>
                {partUnits.map(unit => <option key={unit} value={unit}>{unit}</option>)}
              </select>}
            </div>
            : partType === 'equationBuilder' ? <div>
              <div aria-live="polite" style={{ ...input, minHeight: '3.5rem', background: '#f8fafc' }}>
                {String(partValue || '').trim() ? <MathExpression value={partValue} /> : <span style={{ color: '#64748b' }}>Build the equation below</span>}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.5rem' }}>
                {(equationTokens.length ? equationTokens : ['0','1','2','3','4','5','6','7','8','9','+','−','×','÷','=']).map(token =>
                  <button type="button" key={token} disabled={disabled} style={{ ...action, minWidth: '3rem' }} onClick={() => updatePart(`${partValue || ''}${token}`)}>{token}</button>)}
                <button type="button" disabled={disabled || !partValue} style={{ ...action, background: '#475569' }} onClick={() => updatePart(String(partValue).slice(0, -1))}>⌫</button>
                <button type="button" disabled={disabled || !partValue} style={{ ...action, background: '#64748b' }} onClick={() => updatePart('')}>Clear</button>
              </div>
            </div>
            : <input type="text" style={input} value={partValue} disabled={disabled}
              aria-label={`Part ${index + 1} response`} onChange={event => updatePart(event.target.value)} />}
        </div>;
      })}
      <CommitButton disabled={disabled || parts.some((part, index) => {
        const answer = value[String(part.id ?? index + 1)];
        if ((part.questionType || 'multipleChoice') === 'numericResponse' && answer && typeof answer === 'object') {
          return String(answer.value ?? '').trim() === ''
            || (asList(part.Options?.Units).length > 0 && String(answer.unit ?? '').trim() === '');
        }
        return Array.isArray(answer) ? answer.length === 0 : String(answer ?? '').trim() === '';
      })} committed={committed} onClick={() => commit(value)} />
    </div>;
  }

  if (type === 'equationBuilder') {
    const value = String(draft || '');
    const tokens = asList(options.Tokens).length ? asList(options.Tokens) : ['0','1','2','3','4','5','6','7','8','9','x','+','−','×','÷','/','=','(',')','^','√'];
    return <div style={panel}>
      <label style={{ display: 'block', fontWeight: 700, marginBottom: '0.5rem' }}>Build or type the equation</label>
      <input style={input} value={value} disabled={disabled} autoComplete="off" autoCapitalize="off" spellCheck={false}
        aria-label="Equation response" onChange={event => { setDraft(event.target.value); setCommitted(false); }} />
      {value.trim() && <div aria-live="polite" style={{ marginTop: '0.75rem', minHeight: '3rem', padding: '0.75rem', background: '#f8fafc', borderRadius: '0.5rem', fontSize: '1.45rem' }}>
        <span style={{ fontSize: '0.85rem', color: '#475569', marginRight: '0.5rem' }}>Equation preview:</span>
        <MathExpression value={value} />
      </div>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', margin: '0.75rem 0' }}>
        {tokens.map(token => <button type="button" key={token} disabled={disabled} style={{ ...action, minWidth: '3rem' }}
          aria-label={`Insert ${mathExpressionToSpeech(token) || token}`} title={`Insert ${mathExpressionToSpeech(token) || token}`}
          onClick={() => { setDraft(`${value}${token}`); setCommitted(false); }}>{token}</button>)}
        <button type="button" disabled={disabled || !value} style={{ ...action, background: '#475569' }}
          aria-label="Delete last character" title="Delete last character"
          onClick={() => { setDraft(value.slice(0, -1)); setCommitted(false); }}>⌫</button>
        <button type="button" disabled={disabled || !value} style={{ ...action, background: '#64748b' }} onClick={() => { setDraft(''); setCommitted(false); }}>Clear</button>
      </div>
      <CommitButton disabled={disabled || !value.trim()} committed={committed} onClick={() => commit(value)} />
    </div>;
  }

  if (type === 'numberLine') {
    const count = Math.max(1, Math.min(10, Number(options.PointCount || 1)));
    const values = Array.isArray(draft) ? [...draft] : [''];
    while (values.length < count) values.push('');
    const minimum = finiteNumber(options.Min, 0);
    const maximum = finiteNumber(options.Max, 10);
    const step = finiteNumber(options.Step, 1) > 0 ? finiteNumber(options.Step, 1) : 1;
    const ticks = tickValues(minimum, maximum, step);
    const plotLeft = 48;
    const plotRight = 552;
    const updatePoint = (index, nextValue) => {
      const next = values.slice(0, count);
      next[index] = nextValue;
      setDraft(next);
      setCommitted(false);
    };
    const selectFromX = localX => {
      const raw = minimum + ((clamp(localX, plotLeft, plotRight) - plotLeft) / (plotRight - plotLeft)) * (maximum - minimum);
      updatePoint(activePointIndex, snappedValue(raw, minimum, maximum, step));
    };
    return <div style={panel}>
      <p><strong>Range:</strong> <MathExpression value={`${minimum} \\le x \\le ${maximum}`} /></p>
      <p style={{ color: '#475569' }}>Select Point {activePointIndex + 1}, then tap the line or type a value. Arrow keys move by one step.</p>
      <svg viewBox="0 0 600 115" width="100%" style={{ minHeight: '7rem', touchAction: 'manipulation', background: '#f8fafc', borderRadius: '0.5rem' }}
        role="slider" tabIndex={disabled ? -1 : 0} aria-label={`Number line point ${activePointIndex + 1}`}
        aria-valuemin={minimum} aria-valuemax={maximum}
        aria-valuenow={Number.isFinite(rationalNumber(values[activePointIndex])) ? rationalNumber(values[activePointIndex]) : minimum}
        aria-valuetext={Number.isFinite(rationalNumber(values[activePointIndex])) ? String(values[activePointIndex]) : 'No point selected'}
        onClick={event => {
          if (disabled) return;
          const bounds = event.currentTarget.getBoundingClientRect();
          selectFromX(((event.clientX - bounds.left) / bounds.width) * 600);
        }}
        onKeyDown={event => {
          if (disabled || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          event.preventDefault();
          const current = rationalNumber(values[activePointIndex]) ?? minimum;
          updatePoint(activePointIndex, snappedValue(current + (event.key === 'ArrowRight' ? step : -step), minimum, maximum, step));
        }}>
        <line x1={plotLeft} y1="50" x2={plotRight} y2="50" stroke="#0f172a" strokeWidth="3" />
        <path d={`M ${plotLeft} 50 l 10 -6 v 12 z M ${plotRight} 50 l -10 -6 v 12 z`} fill="#0f172a" />
        {ticks.map(tick => {
          const x = plotLeft + ((tick - minimum) / (maximum - minimum)) * (plotRight - plotLeft);
          return <g key={tick}><line x1={x} y1="38" x2={x} y2="62" stroke="#334155" strokeWidth="2" />
            <text x={x} y="84" textAnchor="middle" fontSize="13" fill="#334155">{tick}</text></g>;
        })}
        {values.slice(0, count).map((value, index) => {
          const numeric = rationalNumber(value);
          if (numeric === null || numeric < minimum || numeric > maximum) return null;
          const x = plotLeft + ((numeric - minimum) / (maximum - minimum)) * (plotRight - plotLeft);
          return <g key={`selected-${index}`}><circle cx={x} cy="50" r={index === activePointIndex ? 10 : 8} fill={index === activePointIndex ? '#2563eb' : '#7c3aed'} stroke="#fff" strokeWidth="3" />
            <text x={x} y="19" textAnchor="middle" fontSize="13" fontWeight="700" fill="#1e3a8a">P{index + 1}</text></g>;
        })}
      </svg>
      {values.slice(0, count).map((value, index) => <label key={index} style={row} className="assessment-point-entry">
        <input type="radio" name={`active-number-line-point-${itemKey}`} checked={activePointIndex === index}
          disabled={disabled} onChange={() => setActivePointIndex(index)} aria-label={`Select point ${index + 1}`} />
        Point {index + 1}<input type="text" inputMode="decimal" aria-label={`Point ${index + 1} value`}
          style={input} value={value} disabled={disabled} onFocus={() => setActivePointIndex(index)}
          onChange={event => updatePoint(index, event.target.value)} />
      </label>)}
      <CommitButton disabled={disabled || values.slice(0, count).some(value => value === '')} committed={committed}
        onClick={() => commit(values.slice(0, count))} />
    </div>;
  }

  if (type === 'coordinateGraph') {
    const count = Math.max(1, Math.min(10, Number(options.PointCount || 1)));
    const points = Array.isArray(draft) ? [...draft] : [{ x: '', y: '' }];
    while (points.length < count) points.push({ x: '', y: '' });
    const xMin = finiteNumber(options.XMin, -10);
    const xMax = finiteNumber(options.XMax, 10);
    const yMin = finiteNumber(options.YMin, -10);
    const yMax = finiteNumber(options.YMax, 10);
    const step = finiteNumber(options.Step, 1) > 0 ? finiteNumber(options.Step, 1) : 1;
    const xTicks = tickValues(xMin, xMax, step, 21);
    const yTicks = tickValues(yMin, yMax, step, 21);
    const left = 55; const right = 555; const top = 20; const bottom = 370;
    const toX = value => left + ((value - xMin) / (xMax - xMin)) * (right - left);
    const toY = value => bottom - ((value - yMin) / (yMax - yMin)) * (bottom - top);
    const updateCoordinate = (index, point) => {
      const next = points.slice(0, count);
      next[index] = point;
      setDraft(next);
      setCommitted(false);
    };
    return <div style={panel}>
      <p id={`coordinate-help-${itemKey}`}>Select a point, then tap the coordinate plane or enter its ordered pair. Arrow keys move the active point.</p>
      <svg viewBox="0 0 610 410" width="100%" style={{ minHeight: '20rem', touchAction: 'manipulation', background: '#f8fafc', borderRadius: '0.5rem' }}
        role="application" tabIndex={disabled ? -1 : 0} aria-label={`Coordinate plane for point ${activePointIndex + 1}`}
        aria-roledescription="interactive coordinate plane" aria-describedby={`coordinate-help-${itemKey}`}
        onClick={event => {
          if (disabled) return;
          const bounds = event.currentTarget.getBoundingClientRect();
          const localX = ((event.clientX - bounds.left) / bounds.width) * 610;
          const localY = ((event.clientY - bounds.top) / bounds.height) * 410;
          const x = snappedValue(xMin + ((clamp(localX, left, right) - left) / (right - left)) * (xMax - xMin), xMin, xMax, step);
          const y = snappedValue(yMax - ((clamp(localY, top, bottom) - top) / (bottom - top)) * (yMax - yMin), yMin, yMax, step);
          updateCoordinate(activePointIndex, { x, y });
        }}
        onKeyDown={event => {
          if (disabled || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
          event.preventDefault();
          const current = points[activePointIndex] || { x: 0, y: 0 };
          const x = finiteNumber(current.x, 0) + (event.key === 'ArrowRight' ? step : event.key === 'ArrowLeft' ? -step : 0);
          const y = finiteNumber(current.y, 0) + (event.key === 'ArrowUp' ? step : event.key === 'ArrowDown' ? -step : 0);
          updateCoordinate(activePointIndex, {
            x: snappedValue(x, xMin, xMax, step),
            y: snappedValue(y, yMin, yMax, step)
          });
        }}>
        {xTicks.map(tick => <line key={`xgrid-${tick}`} x1={toX(tick)} y1={top} x2={toX(tick)} y2={bottom} stroke="#dbeafe" strokeWidth="1" />)}
        {yTicks.map(tick => <line key={`ygrid-${tick}`} x1={left} y1={toY(tick)} x2={right} y2={toY(tick)} stroke="#dbeafe" strokeWidth="1" />)}
        <line x1={left} y1={toY(clamp(0, yMin, yMax))} x2={right} y2={toY(clamp(0, yMin, yMax))} stroke="#0f172a" strokeWidth="2.5" />
        <line x1={toX(clamp(0, xMin, xMax))} y1={top} x2={toX(clamp(0, xMin, xMax))} y2={bottom} stroke="#0f172a" strokeWidth="2.5" />
        {xTicks.map(tick => <text key={`xlabel-${tick}`} x={toX(tick)} y={bottom + 23} textAnchor="middle" fontSize="12" fill="#334155">{tick}</text>)}
        {yTicks.map(tick => <text key={`ylabel-${tick}`} x={left - 10} y={toY(tick) + 4} textAnchor="end" fontSize="12" fill="#334155">{tick}</text>)}
        {points.slice(0, count).map((point, index) => {
          const x = Number(point.x); const y = Number(point.y);
          if (!Number.isFinite(x) || !Number.isFinite(y) || x < xMin || x > xMax || y < yMin || y > yMax) return null;
          return <g key={`graph-point-${index}`}><circle cx={toX(x)} cy={toY(y)} r={index === activePointIndex ? 9 : 7}
            fill={index === activePointIndex ? '#2563eb' : '#7c3aed'} stroke="#fff" strokeWidth="2" />
            <text x={toX(x) + 10} y={toY(y) - 10} fontSize="13" fontWeight="700" fill="#1e3a8a">P{index + 1}</text></g>;
        })}
      </svg>
      {points.slice(0, count).map((point, index) => <div key={index} className="assessment-point-entry" style={{ ...row, display: 'grid', gridTemplateColumns: '5rem 1fr 1fr' }}>
        <label><input type="radio" name={`active-coordinate-point-${itemKey}`} checked={activePointIndex === index}
          disabled={disabled} onChange={() => setActivePointIndex(index)} /> <strong>Point {index + 1}</strong></label>
        <input type="number" min={xMin} max={xMax} step={step} aria-label={`Point ${index + 1} x-coordinate`} placeholder="x" style={input} value={point.x} disabled={disabled}
          onFocus={() => setActivePointIndex(index)} onChange={event => updateCoordinate(index, { ...point, x: event.target.value })} />
        <input type="number" min={yMin} max={yMax} step={step} aria-label={`Point ${index + 1} y-coordinate`} placeholder="y" style={input} value={point.y} disabled={disabled}
          onFocus={() => setActivePointIndex(index)} onChange={event => updateCoordinate(index, { ...point, y: event.target.value })} />
      </div>)}
      <CommitButton disabled={disabled || points.slice(0, count).some(point => point.x === '' || point.y === '')} committed={committed}
        onClick={() => commit(points.slice(0, count).map(point => [Number(point.x), Number(point.y)]))} />
    </div>;
  }

  if (type === 'oralReading') {
    return <div style={{ ...panel, borderColor: '#f59e0b', background: '#fffbeb' }}>
      <h3 style={{ marginTop: 0 }}>Teacher-administered oral reading</h3>
      <HighlightedTTS text={options.Passage} speechText={options.PassageSpeech} enabled={ttsEnabled} size="small"
        textStyle={{ fontSize: '1.2rem', lineHeight: 1.7 }} highlightedStyle={{ backgroundColor: '#fef08a' }} />
      <p>This item requires a teacher to record words read, errors, time, and fluency. Student-entered scores are intentionally disabled.</p>
      <CommitButton
        disabled={disabled}
        committed={committed}
        onClick={() => commit('awaiting-teacher-score')}
      />
      <p style={{ fontSize: '0.9rem' }}>Save only after the student has finished reading. The response remains unscored until a teacher records the result.</p>
    </div>;
  }

  return null;
}
